import { BannerStatus, CreativityContentData } from '../types/banner';
import { AvatarContent } from '../types/content';
import { processVideoUrls } from './video-url-utils';

interface StatusInfo {
  label: string;
  message: string;
}

const getVideoDownloadStatus = (creativity: CreativityContentData): StatusInfo | null => {
  if (!('config' in creativity)) return null;

  const videos = processVideoUrls(creativity.config, videoUrl =>
    videoUrl.status ? { status: videoUrl.status } : null
  );

  if (videos.length === 0) return null;

  const pendingCount = videos.filter(v => v.status === 'pending').length;
  const errorCount = videos.filter(v => v.status === 'error').length;
  const readyCount = videos.filter(v => v.status === 'ready').length;

  if (errorCount > 0) {
    return { label: 'Error', message: `Error descargando ${errorCount} video${errorCount > 1 ? 's' : ''}` };
  }

  if (pendingCount > 0) {
    return { label: 'Analizando', message: 'Analizando videos encontrados' };
  }

  if (readyCount === videos.length) {
    return { label: 'Videos listos', message: 'Enviando a procesamiento' };
  }

  return null;
};

const getSocialStatus = (creativity: CreativityContentData): StatusInfo | null => {
  if (creativity.visibleInSocial !== true) return null;

  const ownStatus = (creativity as { status?: string }).status;

  // No dejamos el badge "Enviando…" colgado si la descarga/optimización falló: lo
  // hacemos visible (el usuario puede destildar y reintentar — eso re-encola la descarga).
  if (ownStatus === 'FAILED') {
    return { label: 'Error al enviar', message: 'Falló la descarga u optimización para Social. Destildá y reintentá.' };
  }

  // "En Social" cuando el video ya está optimizado: la creativity lo refleja en `socialStatus`
  // (la entidad video derivada quedó READY); el reel/video lo expresa con su propio `status`.
  const isReady = creativity.socialStatus === 'READY' || ownStatus === 'READY';

  if (isReady) {
    return { label: 'En Social', message: 'Visible en el catálogo de Social' };
  }

  return { label: 'Enviando a Social…', message: 'Optimizando y publicando en Social' };
};

const STATUS_MAP: Record<BannerStatus, StatusInfo> = {
  PROCESSING_IA: { label: 'Analizando', message: 'IA analizando el contenido' },
  PROCESSING_FORM: { label: 'Analizando', message: 'IA analizando el contenido' },
  PENDING: { label: 'Preparando', message: 'Preparando para generación' },
  QUEUED: { label: 'En cola', message: 'Esperando turno de procesamiento' },
  IN_PROGRESS: { label: 'Generando', message: 'Creando el asset' },
  AWAITING_REVIEW: { label: 'Revisión', message: 'Esperando revisión de contenido' },
  DONE: { label: 'Listo', message: 'Listo para usar' },
  ERROR: { label: 'Error', message: 'Error en el procesamiento' },
  INVALID: { label: 'Inválido', message: 'Archivo inválido' },
  FAILED: { label: 'Fallido', message: 'El procesamiento falló' },
  THROTTLED: { label: 'Limitado', message: 'Procesamiento limitado por uso excesivo' }
};

export const getBannerStatusData = (creativity: CreativityContentData | AvatarContent): StatusInfo | null => {
  // Un avatar no va a Social ni se descarga como video: solo su propio estado.
  if (creativity.type === 'avatar') {
    return creativity.status ? STATUS_MAP[creativity.status] : null;
  }

  const contentType: string = creativity.type;
  if ((contentType === 'reel' || contentType === 'video') && creativity.visibleInSocial === true) {
    const social = getSocialStatus(creativity);
    if (social) return social;
  }

  if (creativity.status) {
    const status = creativity.status;

    // Una vez que el banner está listo, el estado de Social es el más relevante.
    if (status === 'DONE') {
      const socialStatus = getSocialStatus(creativity);
      if (socialStatus) return socialStatus;
    }

    return STATUS_MAP[status];
  }

  return getVideoDownloadStatus(creativity);
};
